import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface ChronologyRefreshStatus {
  label: string;
  tooltip: string;
  value?: number;
  /** Nothing is running yet: no progress bar. */
  idle?: boolean;
}

export interface ChronologyFilterSummaryProps {
  eventsCount: number;
  filteredEventsCount: number;
  hasActiveFilters: boolean;
  onClearAllFilters: () => void;
  /** Timeline zoom controls, placed beside the event count. */
  afterCount?: ReactNode;
  trailing?: ReactNode;
}

export function RefreshStatus({ status }: { status: ChronologyRefreshStatus }) {
  return (
    <div className="shrink-0">
      <div className="flex items-center justify-end gap-1.5">
        <span className="text-[11px] text-gray-500">{status.label}</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="shrink-0 text-gray-400 transition-colors hover:text-gray-600"
                aria-label="Projection progress information"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs text-xs leading-relaxed">
              {status.tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {!status.idle && (
        <div className="ml-auto mt-2 h-1 w-32 overflow-hidden rounded-full bg-gray-200">
          <div
            className={
              status.value == null
                ? 'h-full w-1/2 rounded-full bg-indigo-500 animate-pulse'
                : 'h-full rounded-full bg-indigo-500 transition-all duration-700 ease-out'
            }
            style={status.value == null ? undefined : { width: `${status.value}%` }}
          />
        </div>
      )}
    </div>
  );
}

export function ChronologyFilterSummary({
  eventsCount,
  filteredEventsCount,
  hasActiveFilters,
  onClearAllFilters,
  afterCount,
  trailing,
}: ChronologyFilterSummaryProps) {
  const noun = eventsCount === 1 ? 'event' : 'events';
  const isFiltered = hasActiveFilters || filteredEventsCount !== eventsCount;

  return (
    <div className="flex min-h-8 items-center gap-3 text-xs">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {eventsCount > 0 && (
          <span className="shrink-0 tabular-nums text-gray-500">
            {isFiltered ? (
              <>
                Showing <span className="font-medium text-gray-900">{filteredEventsCount}</span> of {eventsCount} {noun}
              </>
            ) : (
              <>
                <span className="font-medium text-gray-700">{eventsCount}</span> {noun}
              </>
            )}
          </span>
        )}
        {afterCount}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClearAllFilters}
            className="shrink-0 font-medium text-gray-500 underline decoration-gray-300 underline-offset-2 hover:text-gray-900 hover:decoration-gray-500"
          >
            Clear all filters
          </button>
        )}
      </div>
      {trailing}
    </div>
  );
}
